import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { createTemplate } from '../../../services/templateService';
import { formatCurrency } from '../../../services/campaignService';

const TemplateManager = ({ 
  genre, 
  templates, 
  loading, 
  onLoadTemplate, 
  onClose,
  currentCampaignData 
}) => {
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const handleSaveAsTemplate = async () => {
    if (!templateName?.trim()) {
      alert('Please enter a template name');
      return;
    }

    try {
      setSaving(true);
      
      const templateData = {
        templateName: templateName?.trim(),
        genre: currentCampaignData?.genre,
        description: templateDescription?.trim() || `Custom ${currentCampaignData?.genre} campaign template`,
        objectives: currentCampaignData?.objectives,
        budget: currentCampaignData?.budget,
        costPerEngagement: currentCampaignData?.costPerEngagement,
        paymentSchedule: currentCampaignData?.paymentSchedule,
        contentTypes: currentCampaignData?.contentTypes,
        creativeGuidelines: currentCampaignData?.creativeGuidelines,
        hashtags: currentCampaignData?.hashtags,
        minFollowers: currentCampaignData?.minFollowers,
        maxFollowers: currentCampaignData?.maxFollowers,
        minEngagement: currentCampaignData?.minEngagement,
        contentSpecialization: currentCampaignData?.contentSpecialization,
        isPublic: isPublic
      };

      await createTemplate(templateData);
      alert('Template saved successfully!');
      setShowSaveForm(false);
      setTemplateName('');
      setTemplateDescription('');
      setIsPublic(false);
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const userTemplates = templates?.filter(t => !t?.is_public) || [];
  const publicTemplates = templates?.filter(t => t?.is_public) || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Campaign Templates</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {genre ? `${genre?.charAt(0)?.toUpperCase() + genre?.slice(1)} templates` : 'Select a genre first'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!genre ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-foreground font-medium mb-2">No Genre Selected</p>
              <p className="text-muted-foreground text-sm">Please select a genre in Campaign Basics to view templates</p>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading templates...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Save Current as Template */}
              {!showSaveForm ? (
                <div className="bg-primary/10 border border-primary rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="Save" size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground">Save Current Configuration</p>
                        <p className="text-sm text-muted-foreground">Save your current settings as a reusable template</p>
                      </div>
                    </div>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setShowSaveForm(true)}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Save as Template
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-muted rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground">Save New Template</h3>
                    <button
                      onClick={() => setShowSaveForm(false)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>
                  <Input
                    label="Template Name"
                    placeholder="e.g., My Pop Campaign Template"
                    value={templateName}
                    onChange={(e) => setTemplateName(e?.target?.value)}
                    required
                  />
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Description (Optional)
                    </label>
                    <textarea
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      placeholder="Describe when to use this template..."
                      value={templateDescription}
                      onChange={(e) => setTemplateDescription(e?.target?.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isPublic"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e?.target?.checked)}
                      className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                    />
                    <label htmlFor="isPublic" className="text-sm text-foreground">
                      Make this template public (share with other artists)
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      onClick={handleSaveAsTemplate}
                      disabled={saving}
                      iconName="Save"
                      iconPosition="left"
                      fullWidth
                    >
                      {saving ? 'Saving...' : 'Save Template'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowSaveForm(false)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* User's Templates */}
              {userTemplates?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="User" size={18} />
                    My Templates ({userTemplates?.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {userTemplates?.map((template) => (
                      <TemplateCard
                        key={template?.id}
                        template={template}
                        onLoad={() => onLoadTemplate(template)}
                        isOwn={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Public Templates */}
              {publicTemplates?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Globe" size={18} />
                    Public Templates ({publicTemplates?.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {publicTemplates?.map((template) => (
                      <TemplateCard
                        key={template?.id}
                        template={template}
                        onLoad={() => onLoadTemplate(template)}
                        isOwn={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* No Templates */}
              {templates?.length === 0 && (
                <div className="text-center py-12 bg-muted rounded-lg">
                  <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-foreground font-medium mb-2">No Templates Found</p>
                  <p className="text-muted-foreground text-sm mb-4">
                    Save your first template to reuse campaign configurations
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} fullWidth>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const TemplateCard = ({ template, onLoad, isOwn }) => {
  return (
    <div className="border border-border rounded-lg p-4 bg-card hover:border-primary/50 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-semibold text-foreground mb-1">{template?.template_name}</h4>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {template?.description || 'No description'}
          </p>
        </div>
        {isOwn && (
          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
            Mine
          </span>
        )}
      </div>

      <div className="space-y-2 mb-4">
        {template?.budget > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="DollarSign" size={14} className="text-muted-foreground" />
            <span className="text-foreground font-medium">{formatCurrency(template?.budget)}</span>
          </div>
        )}
        {template?.content_types?.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Image" size={14} className="text-muted-foreground" />
            <span className="text-foreground">{template?.content_types?.join(', ')}</span>
          </div>
        )}
        {template?.min_followers && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Users" size={14} className="text-muted-foreground" />
            <span className="text-foreground">
              {template?.min_followers?.toLocaleString()}+ followers
            </span>
          </div>
        )}
        {template?.use_count > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="TrendingUp" size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">Used {template?.use_count} times</span>
          </div>
        )}
      </div>

      <Button
        variant="default"
        size="sm"
        onClick={onLoad}
        iconName="Download"
        iconPosition="left"
        fullWidth
      >
        Load Template
      </Button>
    </div>
  );
};

export default TemplateManager;